// Registry-related filters (Compliance → Registry list page).
//
// These are SEPARATE from the licenseFilters even though both ultimately
// resolve against the same `lm_registry` table. The license list filters
// (`licenseKeywords`, `licenseType`, etc.) target `client_license` rows
// joined to the registry; the registry list filters target the registry
// directly. Names mirror that distinction (`registry*`) and the registry
// service (`apps/crm-api/src/modules/licenses/services/registry.service.ts`)
// reads them off `RegistryQueryDto.filters` to build raw SQL — not via
// `applyFiltersToQueryBuilder`, since `lm_registry` has no TypeORM entity.

import { createKeywordFilter } from './factories';
import type { FilterRegistry } from './types';

const REGISTRY_LIST_KEYS: ReadonlyArray<string> = ['registry'];

export const registryFilters: FilterRegistry = {
	registryKeywords: {
		// FULLTEXT search backed by `idx_ft_search` on lm_registry. The
		// service does its own boolean-mode token sanitization so we don't
		// declare `fulltextFields` here — that would route through the
		// shared TypeORM filter builder which doesn't apply to raw SQL.
		...createKeywordFilter({
			label: 'Search',
			placeholder: 'Business name, license #, city...',
			objectTable: 'lm_registry',
			listKeys: [...REGISTRY_LIST_KEYS],
		}),
		queryParamName: 'registryKeywords',
	},

	registryType: {
		type: 'multi-select',
		label: 'Type',
		placeholder: 'Select type...',
		colorScheme: 'purple',
		searchable: true,
		loadOnFocus: true,
		displayFields: {
			id: 'id' as keyof unknown,
			primary: 'label' as keyof unknown,
		},
		// Endpoint returns the distinct license_type values surfaced in
		// the registry list (no default filter is applied — the list
		// loads unfiltered and the user picks types from the full set).
		apiEndpoint: '/licenses/registry/type/options',
		queryParamName: 'registryType',
		multiple: true,
		valueExtractor: { property: 'id' },
		queryMapping: {
			objectTable: 'lm_registry',
			fieldName: 'license_type',
			joinType: 'custom',
		},
		format: 'array',
		listKeys: [...REGISTRY_LIST_KEYS],
	},

	registryStatus: {
		type: 'multi-select',
		label: 'Status',
		placeholder: 'Select status...',
		colorScheme: 'orange',
		searchable: false,
		loadOnFocus: false,
		displayFields: {
			id: 'id' as keyof unknown,
			primary: 'label' as keyof unknown,
		},
		// Static option set — matches the 7 buckets the normalization
		// migration writes to lm_registry.license_status_normalized (see
		// scripts/license-management/migrations/013-add-status-normalization.sql).
		// Mirrors the licenseStatus filter on the /licenses list so reps
		// trained on one surface can apply the same vocabulary on the other.
		options: [
			{ id: 'Active', label: 'Active' },
			{ id: 'Expired', label: 'Expired' },
			{ id: 'Pending', label: 'Pending' },
			{ id: 'Revoked', label: 'Revoked' },
			{ id: 'Suspended', label: 'Suspended' },
			{ id: 'Cancelled', label: 'Cancelled' },
			{ id: 'Other', label: 'Other' },
		],
		queryParamName: 'registryStatus',
		multiple: true,
		valueExtractor: { property: 'id' },
		queryMapping: {
			objectTable: 'lm_registry',
			fieldName: 'license_status_normalized',
			joinType: 'custom',
		},
		format: 'array',
		listKeys: [...REGISTRY_LIST_KEYS],
	},

	registryState: {
		type: 'multi-select',
		label: 'State',
		placeholder: 'Select state...',
		colorScheme: 'green',
		searchable: true,
		loadOnFocus: true,
		displayFields: {
			id: 'id' as keyof unknown,
			primary: 'label' as keyof unknown,
		},
		apiEndpoint: '/licenses/registry/state/options',
		queryParamName: 'registryState',
		multiple: true,
		valueExtractor: { property: 'id' },
		queryMapping: {
			objectTable: 'lm_registry',
			fieldName: 'state_code',
			joinType: 'custom',
		},
		format: 'array',
		listKeys: [...REGISTRY_LIST_KEYS],
	},

	registryMatchStatus: {
		type: 'radio',
		label: 'Match status',
		colorScheme: 'blue',
		searchable: false,
		loadOnFocus: false,
		displayFields: {
			id: 'id' as keyof unknown,
			primary: 'label' as keyof unknown,
		},
		queryParamName: 'registryMatchStatus',
		multiple: false,
		options: [
			{ id: 'all', label: 'All' },
			{ id: 'matched', label: 'Matched only' },
			{ id: 'unmatched', label: 'Unmatched only' },
		],
		defaultValue: { id: 'all', label: 'All' },
		queryMapping: {
			objectTable: 'lm_registry',
			joinType: 'custom',
		},
		// Convention parity with every other radio filter in the repo (see
		// licenseHasDisciplinary / licenseHasFiles in license.ts). Today this
		// works via FilterService defaults; declaring it explicitly guards
		// against a future default change.
		format: 'single',
		valueExtractor: {
			property: 'id',
		},
		listKeys: [...REGISTRY_LIST_KEYS],
	},
};
